/**
 * Off-canvas mobile navigation, desktop mega dropdown, mobile accordion,
 * smooth scrolling to in-page anchors (with fixed header offset).
 */

export function initNavigation() {
	const body = document.body;
	const nav = document.getElementById( 'site-nav' );
	const openBtn = document.getElementById( 'site-nav-open' );
	const closeBtn = document.getElementById( 'site-nav-close' );
	if ( ! nav || ! openBtn || ! closeBtn ) {
		return;
	}

	let open = false;
	let scrollY = 0;

	const setExpanded = ( value ) => {
		[ openBtn, closeBtn ].forEach( ( b ) => b.setAttribute( 'aria-expanded', String( value ) ) );
		nav.setAttribute( 'aria-hidden', String( ! value ) );
	};

	const show = () => {
		scrollY = window.scrollY;
		body.style.top = `${ -scrollY }px`;
		body.classList.add( 'is-site-nav-open' );
		setExpanded( true );
		open = true;
	};

	const hide = () => {
		body.classList.remove( 'is-site-nav-open' );
		body.style.top = '';
		window.scrollTo( 0, scrollY );
		setExpanded( false );
		open = false;
	};

	const toggle = () => ( open ? hide() : show() );
	openBtn.addEventListener( 'click', toggle );
	closeBtn.addEventListener( 'click', toggle );

	document.addEventListener( 'click', ( e ) => {
		if ( open && ! nav.contains( e.target ) && ! openBtn.contains( e.target ) && ! closeBtn.contains( e.target ) ) {
			hide();
		}
	} );
	document.addEventListener( 'keydown', ( e ) => {
		if ( open && e.key === 'Escape' ) {
			hide();
			openBtn.focus();
		}
	} );
}

export function initDropdown() {
	const desktop = window.matchMedia( '(min-width: 64em)' );
	const hoverCapable = window.matchMedia( '(hover: hover)' );
	let current = null;
	let openTimer = null;
	let closeTimer = null;

	const close = ( dropdown, trigger ) => {
		if ( ! dropdown || dropdown.matches( ':hover' ) || trigger.matches( ':hover' ) ) {
			return;
		}
		dropdown.classList.remove( 'is-open' );
		dropdown.setAttribute( 'aria-hidden', 'true' );
		trigger.classList.remove( 'is-open' );
		trigger.setAttribute( 'aria-expanded', 'false' );
		if ( current === dropdown ) {
			current = null;
		}
	};

	const openFn = ( dropdown, trigger ) => {
		if ( current && current !== dropdown ) {
			current.classList.remove( 'is-open' );
			current.setAttribute( 'aria-hidden', 'true' );
		}
		dropdown.classList.add( 'is-open' );
		dropdown.setAttribute( 'aria-hidden', 'false' );
		trigger.classList.add( 'is-open' );
		trigger.setAttribute( 'aria-expanded', 'true' );
		current = dropdown;
	};

	document.querySelectorAll( '.top-bar__menu .is-dropdown' ).forEach( ( trigger ) => {
		const dropdown = document.getElementById( trigger.dataset.dropdown );
		if ( ! dropdown ) {
			return;
		}
		const scheduleClose = () => {
			clearTimeout( openTimer );
			closeTimer = setTimeout( () => close( dropdown, trigger ), 100 );
		};

		trigger.addEventListener( 'mouseenter', () => {
			if ( ! desktop.matches ) {
				return;
			}
			clearTimeout( closeTimer );
			openTimer = setTimeout( () => openFn( dropdown, trigger ), 150 );
		} );
		trigger.addEventListener( 'mouseleave', scheduleClose );
		dropdown.addEventListener( 'mouseleave', scheduleClose );

		// Keyboard (detail === 0) and touch devices toggle on click; mouse users use hover.
		trigger.addEventListener( 'click', ( e ) => {
			e.preventDefault();
			if ( e.detail !== 0 && hoverCapable.matches ) {
				return;
			}
			if ( dropdown.classList.contains( 'is-open' ) ) {
				dropdown.classList.remove( 'is-open' );
				dropdown.setAttribute( 'aria-hidden', 'true' );
				trigger.setAttribute( 'aria-expanded', 'false' );
				trigger.classList.remove( 'is-open' );
				current = null;
			} else {
				openFn( dropdown, trigger );
			}
		} );
		document.addEventListener( 'keydown', ( e ) => {
			if ( e.key === 'Escape' && current === dropdown ) {
				dropdown.classList.remove( 'is-open' );
				dropdown.setAttribute( 'aria-hidden', 'true' );
				trigger.setAttribute( 'aria-expanded', 'false' );
				trigger.classList.remove( 'is-open' );
				current = null;
				trigger.focus();
			}
		} );
	} );
}

export function initAccordion() {
	const triggers = document.querySelectorAll( '.site-nav .is-dropdown' );
	triggers.forEach( ( item ) => {
		const panel = item.nextElementSibling;
		if ( ! panel ) {
			return;
		}
		item.setAttribute( 'aria-expanded', 'false' );
		panel.setAttribute( 'aria-hidden', 'true' );

		item.addEventListener( 'click', ( e ) => {
			e.preventDefault();
			const isOpen = item.getAttribute( 'aria-expanded' ) === 'true';
			triggers.forEach( ( other ) => {
				if ( other !== item && other.nextElementSibling ) {
					other.setAttribute( 'aria-expanded', 'false' );
					other.nextElementSibling.setAttribute( 'aria-hidden', 'true' );
					other.nextElementSibling.classList.remove( 'is-open' );
				}
			} );
			item.setAttribute( 'aria-expanded', String( ! isOpen ) );
			panel.setAttribute( 'aria-hidden', String( isOpen ) );
			panel.classList.toggle( 'is-open', ! isOpen );
		} );
	} );
}

export function initHashScroll( offset ) {
	const scrollToEl = ( el ) =>
		window.scrollTo( {
			top: el.getBoundingClientRect().top + window.scrollY - offset + 1,
			behavior: window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches ? 'auto' : 'smooth',
		} );

	document.addEventListener( 'click', ( e ) => {
		const a = e.target.closest( 'a[href^="#"]' );
		if ( ! a || a.getAttribute( 'href' ) === '#' || a.classList.contains( 'is-dropdown' ) ) {
			return;
		}
		let target = null;
		try {
			target = document.querySelector( a.getAttribute( 'href' ) );
		} catch ( err ) {
			return;
		}
		if ( target ) {
			e.preventDefault();
			scrollToEl( target );
			history.replaceState( null, '', a.getAttribute( 'href' ) );
		}
	} );

	if ( window.location.hash.length > 1 ) {
		const target = document.getElementById( decodeURIComponent( window.location.hash.slice( 1 ) ) );
		if ( target ) {
			setTimeout( () => scrollToEl( target ), 150 );
		}
	}
}
